<template>
  <div class="cs-carousel">
    <div class="container cs-carousel-container">
      <button
        aria-label="previous"
        @click="prev"
        :disabled="!canBack"
        class="cs-carousel-button cs-carousel-button-prev btn-link"
        type="button"
      >
        <span class="glyphicon glyphicon-chevron-left"></span>
      </button>

      <button
        aria-label="next"
        @click="next"
        :disabled="!canNext"
        class="cs-carousel-button cs-carousel-button-next btn-link"
        type="button"
      >
        <span class="glyphicon glyphicon-chevron-right"></span>
      </button>
    </div>
    <div class="cs-carousel-viewport" ref="viewport" @scroll.passive="onscroll()">
      <!-- The prescence or absense of the wrapper affects IE11 -->
      <div class="cs-carousel-content-wrapper">
        <div
          class="cs-carousel-content"
          ref="content"
          @mousedown="onmousedown()"
          @touchstart="ontouchstart()"
          @touchend="ontouchend()"
        >
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import ScrollBooster from "./scrollbooster";

const viewport = ref<HTMLElement | null>(null);
const content = ref<HTMLElement | null>(null);
const scrollBooster = ref<ScrollBooster | null>(null);
const lastX = ref(0);
const scrollPositionX = ref(0);
const canBack = ref(true);
const canNext = ref(true);
const touching = ref(false);
const scrollDistance = 400;

const ontouchstart = () => {
  touching.value = true;
  scrollBooster.value?.destroy();
  scrollBooster.value = null;
};

const ontouchend = () => {
  touching.value = false;
  if (!scrollBooster.value) {
    const left = viewport.value?.scrollLeft || 0;
    scrollBooster.value = new ScrollBooster({
      viewport: viewport.value as HTMLElement,
      content: content.value as HTMLElement,
      scrollMode: "native",
      direction: "horizontal",
      onUpdate: (state) => {
        const { x } = state.position;
        const diff = Math.abs(x - lastX.value);
        if (diff !== 0 && diff < 0.05 && scrollBooster.value) {
          scrollBooster.value.isTargetScroll = false;
          scrollBooster.value.velocity = { x: 0, y: 0 };
        }
        lastX.value = x;
      },
    } as any);
    scrollBooster.value.setPosition({ x: left, y: 0 });
  }
};

const onscroll = () => {
  if (scrollBooster.value && scrollBooster.value.position) {
    const boosterX = -scrollBooster.value.position.x;
    const viewportX = viewport.value?.scrollLeft || 0;

    if (Math.abs(viewportX - boosterX) > 1) {
      scrollBooster.value.isTargetScroll = false;
    }
  }
  scrollPositionX.value = viewport.value?.scrollLeft || 0;
  canBack.value = scrollPositionX.value > 10;
  canNext.value =
    (viewport.value?.scrollWidth || 0) - (viewport.value?.clientWidth || 0) - 10 >
    scrollPositionX.value;
};

const onmousedown = () => {
  if (scrollBooster.value) {
    scrollBooster.value.isTargetScroll = false;
  }
};

const next = () => {
  scrollBooster.value?.scrollTo({
    x: (viewport.value?.scrollLeft || 0) + scrollDistance,
    y: 0,
  });
};

const prev = () => {
  scrollBooster.value?.scrollTo({
    x: (viewport.value?.scrollLeft || 0) - scrollDistance,
    y: 0,
  });
};

onMounted(() => {
  ontouchend();
  onscroll();
});

onUnmounted(() => {
  scrollBooster.value?.destroy();
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.cs-carousel {
  width: 100%;

  .cs-carousel-container {
    position: relative;
    height: 0;
  }

  .cs-carousel-viewport {
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    margin-top: -40px;
    margin-bottom: -40px;

    .cs-carousel-content {
      padding-top: 40px;
      padding-bottom: 40px;
    }
  }

  // https://www.w3schools.com/howto/howto_css_hide_scrollbars.asp
  /* Hide scrollbar for Chrome, Safari and Opera */
  .cs-carousel-viewport::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  .cs-carousel-viewport {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  @media (max-width: @screen-sm) {
    .cs-carousel-button {
      display: none;
    }
  }

  .cs-carousel-button {
    position: absolute;
    top: -40px;
    right: 25px;
    left: auto;
    transform: none;
    background: none;
    opacity: 1;
    border-radius: 100px;
    outline: none !important;
    font-size: 24px;

    &:hover {
      background: #ddd;
    }

    .glyphicon {
      position: relative;
      top: 3px;
    }

    &:disabled:hover {
      color: #ddd;
    }

    &:disabled {
      background: none;
      color: #ddd;
      cursor: auto;
    }
  }

  .cs-carousel-button-prev {
    top: -40px;
    right: 65px;
  }

  .cs-carousel-content {
    display: flex;
    & > * {
      flex-grow: 0;
      flex-shrink: 0;
      width: 40vw;
    }
  }

  @media (min-width: @screen-sm) {
    .cs-carousel-content > * {
      width: 750px / 5;
    }

    .cs-carousel-content::before,
    .cs-carousel-content::after {
      content: " ";
      display: block;
      flex-grow: 0;
      flex-shrink: 0;
      width: calc((100% - 750px) / 2);
    }
  }

  @media (min-width: @screen-md) {
    .cs-carousel-content > * {
      width: 970px / 4;
    }
    .cs-carousel-content::before,
    .cs-carousel-content::after {
      width: calc((100% - 970px) / 2);
    }
  }

  @media (min-width: @screen-lg) {
    .cs-carousel-content > * {
      width: 1170px / 5;
    }
    .cs-carousel-content::before,
    .cs-carousel-content::after {
      width: calc((100% - 1170px) / 2);
    }
  }
}
</style>
